/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React, { useMemo, useState, useCallback, memo, useEffect } from 'react'
import colors from '@sportninja/common/constants/appColors'
import Text from '../ts-components/Text'
import {
  HeartOutlined,
  HeartFilled,
  DeleteOutlined,
  EditOutlined,
  FlagOutlined,
} from '@ant-design/icons'
import { Dropdown, Image, App } from 'antd'
import sportninjaLogo from './sportninja-post.png'
import canlanLogo from './canlan-post.png'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import {
  getFeedPostDate,
  getImageThumbnailId,
} from '@sportninja/common/utils/utils'
import VerifiedIcon from './VerifiedIcon'
import { paragraphSmall, weightRegular } from '../css'
import TopComment from './TopComment'
import { Reply } from '@sportninja/common/hooks/useSocialFeed'
import { MoreIcon } from './MoreIcon'
import AddComment from './AddComment'
import FeedItemDetailsModal from './FeedItemDetailsModal'
import DeleteModal from '../Modal/DeleteModal'
import req from '@sportninja/common/api/request'
import ExternalLinkIcon from './ExternalLinkIcon'
import ShareIcon from './ShareIcon'

const likeComment = async (commentId: string) => {
  await req(`/social-feed-reply/${commentId}/like`, {
    method: 'POST',
  })
  return true
}

const dislikeComment = async (commentId: string) => {
  await req(`/social-feed-reply/${commentId}/like`, {
    method: 'DELETE',
  })
  return true
}

const deleteComment = async (commentId: string) => {
  await req(`/social-feed-reply/${commentId}`, {
    method: 'DELETE',
  })
  return true
}

// Improved TypeScript interfaces
interface FeedItemProps {
  setIsModalOpen: (open: boolean) => void
  entityType: 'team' | 'schedule' | 'organization'
  title: string
  body: string
  imageUrl?: string
  linkUrl?: string
  likesCount: number
  commentsCount?: number
  createdAt: string
  avatarUrl?: string
  onCommentClick?: () => void
  onDeleteClick: () => void
  onReplySubmit?: (replyContent: string) => void
  onLikeClick: () => void
  onReportClick?: () => void
  entityName: string
  canEdit?: boolean
  canDelete?: boolean
  isAppPost?: boolean
  userAvatarUrl?: string
  entityId: string
  isLiked?: boolean
  userId?: string
  userName?: string
  userProfilePicture?: string
  onEditClick?: () => void
  comments?: Reply[]
  postId: string
  selfUserId?: string
  selfUserName?: string
  fetchPosts: (pageNumber: number, shouldDisplayLoading?: boolean) => void
  canReply?: boolean
  shouldAutoOpenModal?: boolean
  onModalAutoOpened?: () => void
}

interface DropdownMenuItem {
  label: string
  key: string
  icon: React.ReactNode
  onClick?: () => void
  danger?: boolean
}

// Comment icon component with custom SVG
const CommentIcon: React.FC = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='16'
    height='17'
    viewBox='0 0 16 17'
    fill='none'
  >
    <path
      d='M4.66671 9.83331H11.3334C11.5223 9.83331 11.6805 9.76931 11.808 9.64131C11.936 9.51376 12 9.35554 12 9.16665C12 8.97776 11.936 8.81931 11.808 8.69131C11.6805 8.56376 11.5223 8.49998 11.3334 8.49998H4.66671C4.47782 8.49998 4.31937 8.56376 4.19137 8.69131C4.06382 8.81931 4.00004 8.97776 4.00004 9.16665C4.00004 9.35554 4.06382 9.51376 4.19137 9.64131C4.31937 9.76931 4.47782 9.83331 4.66671 9.83331ZM4.66671 7.83331H11.3334C11.5223 7.83331 11.6805 7.76931 11.808 7.64131C11.936 7.51376 12 7.35554 12 7.16665C12 6.97776 11.936 6.81931 11.808 6.69131C11.6805 6.56376 11.5223 6.49998 11.3334 6.49998H4.66671C4.47782 6.49998 4.31937 6.56376 4.19137 6.69131C4.06382 6.81931 4.00004 6.97776 4.00004 7.16665C4.00004 7.35554 4.06382 7.51376 4.19137 7.64131C4.31937 7.76931 4.47782 7.83331 4.66671 7.83331ZM4.66671 5.83331H11.3334C11.5223 5.83331 11.6805 5.76954 11.808 5.64198C11.936 5.51398 12 5.35554 12 5.16665C12 4.97776 11.936 4.81931 11.808 4.69131C11.6805 4.56376 11.5223 4.49998 11.3334 4.49998H4.66671C4.47782 4.49998 4.31937 4.56376 4.19137 4.69131C4.06382 4.81931 4.00004 4.97776 4.00004 5.16665C4.00004 5.35554 4.06382 5.51398 4.19137 5.64198C4.31937 5.76954 4.47782 5.83331 4.66671 5.83331ZM13.5334 14.0333L12 12.5H2.66671C2.30004 12.5 1.98626 12.3695 1.72537 12.1086C1.46404 11.8473 1.33337 11.5333 1.33337 11.1666V3.16665C1.33337 2.79998 1.46404 2.48598 1.72537 2.22465C1.98626 1.96376 2.30004 1.83331 2.66671 1.83331H13.3334C13.7 1.83331 14.014 1.96376 14.2754 2.22465C14.5363 2.48598 14.6667 2.79998 14.6667 3.16665V13.55C14.6667 13.85 14.5307 14.0582 14.2587 14.1746C13.9863 14.2915 13.7445 14.2444 13.5334 14.0333ZM2.66671 3.16665V11.1666H12.55L13.3334 11.95V3.16665H2.66671Z'
      fill='#F2B400'
    />
  </svg>
)

// Extracted CSS styles to prevent recreation on every render
const feedItemContainerStyles = css`
  background-color: ${colors.SECONDARY_300};
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
`

const headerStyles = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`

const profileLinkStyles = css`
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
`

const avatarContainerStyles = css`
  position: relative;
  width: 40px;
  height: 40px;
`

const profileInfoStyles = css`
  display: flex;
  align-items: center;
  gap: 4px;
`

const moreIconStyles = css`
  cursor: pointer;
`

const contentStyles = css`
  margin-bottom: 8px;
`

const postBodyStyles = css`
  ${paragraphSmall}
  ${weightRegular}
  line-height: 20.3px;
  color: ${colors.NEUTRAL_20};
  white-space: pre-wrap;
`

const imageContainerStyles = css`
  margin-bottom: 16px;
  width: 100%;
  height: 100%;
  border-radius: 8px;
  margin-top: 16px;
  justify-content: center;
  align-items: center;
  display: flex;
  background-color: ${colors.SECONDARY_400};
  overflow: hidden;
  .ant-image {
    width: 100% !important;
    height: 100% !important;
    border-radius: 8px !important;
  }
  .ant-image .ant-image-img {
    height: 100% !important;
    object-fit: contain !important;
  }
`

const actionsStyles = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  margin-bottom: 16px;
  margin-top: 16px;
`

const actionsGroupStyles = css`
  display: flex;
  gap: 8px;
`

const actionButtonStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  border-radius: 8px;
  background: ${colors.SECONDARY_200};
  padding: 8px;
`

const linkButtonStyles = css`
  text-decoration: none;
  display: flex;
  padding: 8px 8px;
  align-items: center;
  gap: 8px;
  border-radius: 8px;
  border: 1px solid ${colors.PRIMARY};
`

const linkTextStyles = `
  display: flex;
  align-items: center;
  gap: 4px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`

// Constants for placeholder images
const PLACEHOLDER_IMAGES = {
  user: '/images/generic_placeholders/placeholder_player.png',
  organization: '/images/generic_placeholders/placeholder_organization.png',
  team: '/images/generic_placeholders/placeholder_team.png',
  schedule: '/images/generic_placeholders/placeholder_competition.png',
} as const

// Utility function to convert markdown to HTML with improved type safety and performance
const convertMarkdownToHtml = (markdown: string): string => {
  if (!markdown) {
    return ''
  }

  // Helper function to escape HTML special characters
  const escapeHtml = (text: string): string => {
    const htmlEntities: Record<string, string> = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    }
    return text.replace(/[&<>"']/g, (char) => htmlEntities[char])
  }

  let processedMarkdown = markdown

  // Convert headers
  processedMarkdown = processedMarkdown.replace(
    /^(#{1,6})\s(.+)$/gm,
    (_match, hashes, content) => {
      const level = hashes.length
      return `<h${level}>${escapeHtml(content)}</h${level}>`
    }
  )

  // Convert bold text
  processedMarkdown = processedMarkdown.replace(
    /\*(.*?)\*/g,
    '<strong>$1</strong>'
  )
  processedMarkdown = processedMarkdown.replace(
    /<strong>/g,
    '<span style="font-weight: bold;">'
  )
  processedMarkdown = processedMarkdown.replace(/<\/strong>/g, '</span>')

  // Convert italic text
  processedMarkdown = processedMarkdown.replace(/\b_(.*?)_\b/g, '<em>$1</em>')
  processedMarkdown = processedMarkdown.replace(
    /<em>/g,
    '<span style="font-style: italic;">'
  )
  processedMarkdown = processedMarkdown.replace(/<\/em>/g, '</span>')

  // Convert strikethrough text
  processedMarkdown = processedMarkdown.replace(/~(.*?)~/g, '<s>$1</s>')
  processedMarkdown = processedMarkdown.replace(
    /<s>/g,
    '<span style="text-decoration: line-through;">'
  )
  processedMarkdown = processedMarkdown.replace(/<\/s>/g, '</span>')

  // Convert code blocks
  processedMarkdown = processedMarkdown.replace(
    /```(.*?)```/g,
    '<pre><code>$1</code></pre>'
  )
  processedMarkdown = processedMarkdown.replace(
    /<pre>/g,
    '<span style="font-family: monospace;">'
  )
  processedMarkdown = processedMarkdown.replace(/<\/pre>/g, '</span>')

  // Convert links
  processedMarkdown = processedMarkdown.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    '<a href="$2">$1</a>'
  )

  // Convert unordered lists
  processedMarkdown = processedMarkdown.replace(
    /^\s*[-*+]\s(.+)$/gm,
    '<li>$1</li>'
  )
  processedMarkdown = processedMarkdown.replace(
    /(<li>.*<\/li>)/s,
    '<ul>$1</ul>'
  )

  // Convert ordered lists
  processedMarkdown = processedMarkdown.replace(
    /^\s*\d+\.\s(.+)$/gm,
    '<li>$1</li>'
  )
  processedMarkdown = processedMarkdown.replace(
    /(<li>.*<\/li>)/s,
    '<ol>$1</ol>'
  )

  // Convert inline code
  processedMarkdown = processedMarkdown.replace(/`([^`]+)`/g, '<code>$1</code>')

  // Convert paragraphs (must be done last)
  processedMarkdown = processedMarkdown.replace(
    /^(?!<[uo]l|<li|<h\d|<pre)(.+)$/gm,
    '<p>$1</p>'
  )

  return processedMarkdown
}

// Utility function to parse URL host
const parseUrlHost = (url: string): string => {
  try {
    const parsedUrl = new URL(url)
    return parsedUrl.host.replace(/^www\./, '')
  } catch (error) {
    console.error('Error parsing URL:', error)
    return url
  }
}

// Utility function to get placeholder image
const getPlaceholderImage = (
  entityType: FeedItemProps['entityType'],
  isUser = false
): string => {
  if (isUser) {
    return PLACEHOLDER_IMAGES.user
  }
  return PLACEHOLDER_IMAGES[entityType]
}

// Utility function to get avatar styles
const getAvatarStyles = () => css`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  object-fit: contain;
`

const getUserProfileStyles = css`
  position: absolute;
  bottom: -4px;
  right: -4px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: contain;
  background-color: ${colors.WHITE};
  border: 1px solid ${colors.NEUTRAL_100};
`

const separatorStyles = css`
  width: 100%;
  height: 1px;
  background-color: ${colors.SECONDARY_200};
  margin-top: 8px;
  margin-bottom: 16px;
`

// Memoized components for better performance
const LikeButton = memo<{
  isLiked: boolean
  likesCount: number
  onClick: () => void
}>(({ isLiked, likesCount, onClick }) => (
  <div css={actionButtonStyles} onClick={onClick}>
    {isLiked ? (
      <HeartFilled
        css={css`
          color: ${colors.PRIMARY};
        `}
      />
    ) : (
      <HeartOutlined
        css={css`
          color: ${colors.PRIMARY};
        `}
      />
    )}
    {likesCount > 0 && (
      <Text variant='paragraphXSmall' weight='regular' color={colors.WHITE}>
        {likesCount}
      </Text>
    )}
  </div>
))

LikeButton.displayName = 'LikeButton'

const CommentButton = memo<{
  commentsCount?: number
  onClick?: () => void
}>(({ commentsCount, onClick }) => (
  <div css={actionButtonStyles} onClick={onClick}>
    <CommentIcon />
    {commentsCount && commentsCount > 0 && (
      <Text variant='paragraphXSmall' weight='regular' color={colors.WHITE}>
        {commentsCount}
      </Text>
    )}
  </div>
))

CommentButton.displayName = 'CommentButton'

const ShareButton = memo<{
  onClick: () => void
}>(({ onClick }) => (
  <div css={actionButtonStyles} onClick={onClick}>
    <ShareIcon width={16} height={16} fill={colors.PRIMARY} />
    <Text variant='paragraphXSmall' weight='regular' color={colors.WHITE}>
      Copy Link
    </Text>
  </div>
))

ShareButton.displayName = 'ShareButton'

const ExternalLinkButton = memo<{
  linkUrl: string
  formattedHost: string
}>(({ linkUrl, formattedHost }) => (
  <a
    href={linkUrl}
    target='_blank'
    rel='noopener noreferrer'
    css={linkButtonStyles}
  >
    <ExternalLinkIcon width={12} height={12} fill={colors.PRIMARY} />
    <Text
      variant='paragraphXSmall'
      weight='bold'
      color={colors.PRIMARY}
      style={linkTextStyles}
    >
      {formattedHost}
    </Text>
  </a>
))

ExternalLinkButton.displayName = 'ExternalLinkButton'

// Delete Comment Button component
const DeleteCommentButton = memo<React.ButtonHTMLAttributes<HTMLButtonElement>>(
  ({ ...props }) => {
    return (
      <button
        css={css`
          display: flex;
          padding: 8px 16px 8px 8px;
          justify-content: center;
          align-items: center;
          gap: 8px;
          border-radius: 8px;
          border: 1px solid ${colors.ERROR_200};
          background: transparent;
          cursor: pointer;
          font-family: ${isCanlan
            ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif !important;'
            : 'BarlowCondensed, Arial, Helvetica, sans-serif !important;'};
          display: none;
        `}
        {...props}
      >
        <DeleteOutlined
          css={css`
            color: ${colors.ERROR_200};
          `}
        />
        <Text variant='paragraphMedium' weight='semiBold' color={colors.WHITE}>
          DELETE COMMENT
        </Text>
      </button>
    )
  }
)

DeleteCommentButton.displayName = 'DeleteCommentButton'

const FeedItem: React.FC<FeedItemProps> = ({
  userProfilePicture,
  entityId,
  entityType,
  title,
  body,
  imageUrl,
  linkUrl,
  likesCount,
  createdAt,
  avatarUrl,
  onDeleteClick,
  onLikeClick,
  onReportClick,
  entityName,
  canEdit = false,
  canDelete = false,
  isAppPost = false,
  isLiked: initialIsLiked = false,
  userName,
  userId,
  onEditClick,

  commentsCount,
  comments = [],
  postId,
  selfUserId,
  selfUserName,
  fetchPosts,
  canReply = true,
  shouldAutoOpenModal = false,
  onModalAutoOpened,
}) => {
  const topComment = useMemo(() => {
    return comments.length > 0 ? comments[comments.length - 1] : null
  }, [comments])

  const isTopCommentFromSelfUser = useMemo(() => {
    return topComment?.user?.id === selfUserId
  }, [topComment, selfUserId])

  // Local state for optimistic UI updates
  const [isLiked, setIsLiked] = useState(initialIsLiked)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [commentToDelete, setCommentToDelete] = useState<string | null>(null)

  const { message } = App.useApp()

  // Sync local isLiked state with prop when it changes (e.g., after feed refresh)
  useEffect(() => {
    setIsLiked(initialIsLiked)
  }, [initialIsLiked])

  // Memoized computed values
  const isOrgOrSchedulePost = useMemo(
    () => entityType === 'organization' || entityType === 'schedule',
    [entityType]
  )

  const htmlContent = useMemo(() => convertMarkdownToHtml(body), [body])

  const navigationUrl = useMemo(() => {
    if (isAppPost) {
      return undefined
    }
    if (isOrgOrSchedulePost) {
      return `/${
        entityType === 'organization' ? 'org' : 'schedule'
      }/${entityId}`
    }
    return `/team/${entityId}`
  }, [entityId, entityType, isAppPost, isOrgOrSchedulePost])

  const formattedHost = useMemo(() => {
    return linkUrl ? parseUrlHost(linkUrl) : null
  }, [linkUrl])

  const avatarSrc = useMemo(() => {
    if (isAppPost) {
      return isCanlan ? canlanLogo : sportninjaLogo
    }
    return avatarUrl || getPlaceholderImage(entityType)
  }, [isAppPost, avatarUrl, entityType])

  const userProfileSrc = useMemo(() => {
    return userProfilePicture || getPlaceholderImage(entityType, true)
  }, [userProfilePicture, entityType])

  const formattedDate = useMemo(() => {
    return getFeedPostDate(createdAt)
  }, [createdAt])

  const displayName = useMemo(() => {
    return entityName || 'SportNinja'
  }, [entityName])

  const dateText = useMemo(() => {
    if (userName) {
      return `${userName} - ${formattedDate}`
    }
    return formattedDate
  }, [userName, formattedDate])

  // Memoized dropdown menu items
  const dropdownItems = useMemo((): DropdownMenuItem[] => {
    const items: DropdownMenuItem[] = []

    if (canEdit) {
      items.push({
        label: 'Edit',
        key: 'edit',
        icon: <EditOutlined />,
        onClick: onEditClick,
      })
    }

    items.push({
      label: 'Report',
      key: 'report',
      icon: <FlagOutlined style={{ color: colors.WHITE }} />,
      onClick: onReportClick,
      danger: false,
    })

    if (canDelete) {
      items.push({
        label: 'Delete',
        key: 'delete',
        icon: <DeleteOutlined style={{ color: colors.ERROR_200 }} />,
        onClick: onDeleteClick,
        danger: true,
      })
    }

    return items
  }, [canEdit, canDelete, onEditClick, onDeleteClick, onReportClick])

  // Memoized event handlers
  const handleLikeClick = useCallback(() => {
    setIsLiked((prev) => !prev)
    onLikeClick()
  }, [onLikeClick])

  const handleShareClick = useCallback(async () => {
    try {
      const entityTypeMap = {
        organization: 'org',
        team: 'team',
        schedule: 'schedule',
      }

      const urlPrefix = entityTypeMap[entityType]
      if (urlPrefix && entityId) {
        const shareUrl = `${window.location.origin}/${urlPrefix}/${entityId}/feed/${postId}`
        await navigator.clipboard.writeText(shareUrl)
        message.info('Link copied to clipboard!')
      }
    } catch (error) {
      console.error('Failed to copy link:', error)
      message.error('Failed to copy link to clipboard')
    }
  }, [entityType, entityId, postId, message])

  const handleOpenModal = useCallback(() => {
    // setIsModalOpenProp(true)
    setIsModalOpen(true)
  }, [])

  const handleCloseModal = useCallback(() => {
    // setIsModalOpenProp(false)
    setIsModalOpen(false)
  }, [])

  const handleNavigationClick = useCallback(
    (e: React.MouseEvent) => {
      if (isAppPost) {
        e.preventDefault()
      }
    },
    [isAppPost]
  )

  const handleDeleteComment = useCallback(
    async (_shouldForce = false) => {
      if (!commentToDelete) {
        throw new Error('No comment ID provided')
      }

      try {
        await deleteComment(commentToDelete)
        message.success('Comment deleted successfully.')
        fetchPosts(1, false)
        setCommentToDelete(null)
      } catch (err: any) {
        console.error('Failed to delete comment:', err)
        message.error(err?.message || 'Failed to delete comment')
        throw err
      }
    },
    [commentToDelete, message, fetchPosts]
  )

  const handleDeleteCommentClick = useCallback((commentId: string) => {
    setCommentToDelete(commentId)
    // Use a more reliable method to trigger the delete modal
    setTimeout(() => {
      document.getElementById('delete-comment-button')?.click()
    }, 0)
  }, [])

  // Handle auto-opening modal when shouldAutoOpenModal is true
  useEffect(() => {
    if (shouldAutoOpenModal) {
      setIsModalOpen(true)
      onModalAutoOpened?.()
    }
  }, [shouldAutoOpenModal, onModalAutoOpened])

  const navigationCursor = useMemo(
    () => (isAppPost ? 'default' : 'pointer'),
    [isAppPost]
  )

  // Render profile link based on post type
  const renderProfileLink = () => {
    const linkContent = isOrgOrSchedulePost ? (
      <>
        <img src={avatarSrc} alt='Entity logo' css={getAvatarStyles()} />
        <div>
          <div css={profileInfoStyles}>
            <Text variant='paragraphMedium' weight='bold' color={colors.WHITE}>
              {displayName}
            </Text>
            {isAppPost && <VerifiedIcon />}
          </div>
          <Text
            variant='paragraphXSmall'
            weight='regular'
            color={colors.NEUTRAL_50}
          >
            {formattedDate}
          </Text>
        </div>
      </>
    ) : (
      <>
        <div css={avatarContainerStyles}>
          <img src={avatarSrc} alt='Entity logo' css={getAvatarStyles()} />
          <img
            src={userProfileSrc}
            alt='User profile picture'
            css={getUserProfileStyles}
          />
        </div>
        <div>
          <div css={profileInfoStyles}>
            <Text variant='paragraphMedium' weight='bold' color={colors.WHITE}>
              {entityName}
            </Text>
          </div>
          <Text
            variant='paragraphXSmall'
            weight='regular'
            color={colors.NEUTRAL_50}
          >
            {dateText}
          </Text>
        </div>
      </>
    )

    return (
      <a
        href={navigationUrl}
        css={[
          profileLinkStyles,
          css`
            cursor: ${navigationCursor};
          `,
        ]}
        onClick={handleNavigationClick}
      >
        {linkContent}
      </a>
    )
  }

  return (
    <>
      <DeleteModal
        title='Delete Comment'
        message='Are you sure you want to delete this comment? This cannot be undone.'
        onDelete={handleDeleteComment}
        Button={(props) => (
          <DeleteCommentButton id='delete-comment-button' {...props} />
        )}
        forceDeleteText={undefined}
        shouldCloseModalAfterDelete={true}
        useForceDelete={false}
        displayBanner={false}
      />

      <div css={feedItemContainerStyles}>
        {/* Header */}
        <div css={headerStyles}>
          {renderProfileLink()}
          <Dropdown
            menu={{ items: dropdownItems }}
            trigger={['click']}
            getPopupContainer={(triggerNode) =>
              triggerNode.parentElement || document.body
            }
            overlayStyle={{ zIndex: 1001 }}
          >
            <span css={moreIconStyles}>
              <MoreIcon />
            </span>
          </Dropdown>
        </div>

        {/* Content */}
        <div css={contentStyles}>
          <Text
            variant='paragraphMedium'
            weight='bold'
            color={colors.WHITE}
            style='margin-bottom: 8px;'
          >
            {title}
          </Text>
          <div
            dangerouslySetInnerHTML={{ __html: htmlContent as string }}
            className='home-post-body'
            css={postBodyStyles}
          />
        </div>

        {/* Image */}
        {imageUrl && (
          <div
            css={imageContainerStyles}
            onClick={handleOpenModal}
            style={{ cursor: 'pointer' }}
          >
            <Image src={imageUrl} preview={false} draggable={false} />
          </div>
        )}

        {/* Actions */}
        <div css={[actionsStyles, !canReply ? { marginBottom: 0 } : {}]}>
          <div css={actionsGroupStyles}>
            <LikeButton
              isLiked={isLiked}
              likesCount={likesCount}
              onClick={handleLikeClick}
            />
            {canReply && commentsCount && commentsCount > 0 ? (
              <CommentButton
                commentsCount={commentsCount}
                onClick={handleOpenModal}
              />
            ) : null}
            <ShareButton onClick={handleShareClick} />
          </div>
          {formattedHost && (
            <ExternalLinkButton
              linkUrl={linkUrl!}
              formattedHost={formattedHost}
            />
          )}
        </div>

        {canReply && topComment && (
          <TopComment
            avatarUrl={
              getImageThumbnailId(topComment.user) || PLACEHOLDER_IMAGES.user
            }
            userName={topComment?.user?.name_full || ''}
            timeAgo={getFeedPostDate(topComment.created_at)}
            comment={topComment.body}
            likesCount={topComment?.like_count || 0}
            isLiked={topComment?.is_liked}
            onLikeClick={() => likeComment(topComment.id)}
            onDislikeClick={() => dislikeComment(topComment.id)}
            canDelete={isTopCommentFromSelfUser}
            onDeleteClick={() => handleDeleteCommentClick(topComment.id)}
          />
        )}

        {/* See All Comments */}
        {canReply && comments.length > 1 && (
          <span
            onClick={handleOpenModal}
            css={css`
              cursor: pointer;
              margin-top: 8px;
              margin-bottom: 8px;
              display: block;
              text-align: center;
            `}
          >
            <Text
              variant='paragraphXSmall'
              weight='regular'
              color={colors.PRIMARY}
            >
              See All Comments
            </Text>
          </span>
        )}

        {/* Separator */}
        {canReply && <div css={separatorStyles} />}

        {/* Add Comment */}
        {canReply && (
          <AddComment
            userAvatarUrl={userProfilePicture}
            onCommentClick={handleOpenModal}
          />
        )}

        {/* Feed Item Details Modal */}
        <FeedItemDetailsModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          userProfilePicture={userProfilePicture}
          entityId={entityId}
          entityType={entityType}
          entityName={entityName}
          title={title}
          body={body}
          imageUrl={imageUrl}
          linkUrl={linkUrl}
          likesCount={likesCount}
          commentsCount={commentsCount}
          createdAt={createdAt}
          avatarUrl={avatarUrl}
          onLikeClick={onLikeClick}
          isAppPost={isAppPost}
          isLiked={isLiked}
          userName={userName}
          onEditClick={onEditClick}
          comments={comments}
          userId={userId}
          userAvatarUrl={userProfilePicture}
          postId={postId}
          selfUserId={selfUserId}
          selfUserName={selfUserName}
          canReply={canReply}
          fetchPosts={fetchPosts}
        />
      </div>
    </>
  )
}

export default memo(FeedItem)
